import React, { useState } from "react";

const ChirpStackStep4 = ({ devices, onComplete, onBack, onSkip }) => {
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResults, setSyncResults] = useState(null);
  const [error, setError] = useState("");

  const handleSync = async () => {
    setSyncLoading(true);
    setError("");

    try {
      const response = await fetch("/api/admin/sensors/chirpstack/sync", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("token"),
        },
        body: JSON.stringify({
          devices: devices,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSyncResults(data);
      } else {
        setError(data.error || "Sync mislukt");
      }
    } catch (error) {
      console.error("Sync fout:", error);
      setError("Er is een fout opgetreden bij het synchroniseren");
    } finally {
      setSyncLoading(false);
    }
  };

  const handleComplete = () => {
    onComplete(syncResults);
  };

  const handleSkipSync = () => {
    onSkip();
  };

  return (
    <div>
      <h4>Stap 4: ChirpStack Synchronisatie</h4>
      <p>Synchroniseer de geïmporteerde sensoren met ChirpStack device tags.</p>

      <div className="alert alert-info">
        <h5><i className="fa fa-sync"></i> Wat doet synchronisatie?</h5>
        <ul className="mb-0">
          <li>🔗 Zoekt gekoppelde kampeerplaatsen voor elke sensor</li>
          <li>🏷️ Voegt <code>place_id</code> en <code>plaats_naam</code> tags toe aan ChirpStack devices</li>
          <li>📊 Update kampeerplaats informatie met sensor details</li>
          <li>🔄 Zorgt voor bidirectionele koppeling tussen sensoren en kampeerplaatsen</li>
        </ul>
      </div>

      {!syncResults && (
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h5><i className="fa fa-list"></i> Devices voor synchronisatie ({devices.length})</h5>
              </div>
              <div className="card-body" style={{ maxHeight: "300px", overflowY: "auto" }}>
                {devices.map((device, index) => (
                  <div key={index} className="device-card border-bottom pb-2 mb-2">
                    <div className="row">
                      <div className="col-md-4">
                        <strong>{device.name || `Device ${index + 1}`}</strong>
                        <br />
                        <code style={{ fontSize: "0.85em" }}>{device.devEui}</code>
                      </div>
                      <div className="col-md-4">
                        {device.tags && device.tags.place_id ? (
                          <span className="badge badge-success">
                            <i className="fa fa-check"></i> Heeft place_id
                          </span>
                        ) : (
                          <span className="badge badge-warning">
                            <i className="fa fa-exclamation-triangle"></i> Geen place_id tag
                          </span>
                        )}
                      </div>
                      <div className="col-md-4">
                        {device.tags && Object.keys(device.tags).length > 0 ? (
                          <div>
                            <small><strong>Current tags:</strong></small>
                            <div>
                              {Object.entries(device.tags).map(([key, value], tagIndex) => (
                                <span key={tagIndex} className="badge badge-secondary mr-1" style={{ fontSize: '0.7em' }}>
                                  {key}: {value}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <small className="text-muted">Geen tags</small>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger">
          <strong>Fout:</strong> {error}
        </div>
      )}

      {syncResults && (
        <div className="row">
          <div className="col-md-12">
            <div className="alert alert-success">
              <h5><i className="fa fa-check-circle"></i> Synchronisatie Voltooid!</h5>
              <div className="sync-summary">
                <div className="row">
                  <div className="col-md-3">
                    <div className="card bg-primary text-white">
                      <div className="card-body text-center">
                        <i className="fa fa-sync fa-2x"></i>
                        <h4>{syncResults.summary.synced}</h4>
                        <small>Gesynced</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-success text-white">
                      <div className="card-body text-center">
                        <i className="fa fa-check fa-2x"></i>
                        <h4>{syncResults.summary.unchanged}</h4>
                        <small>Al up-to-date</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-warning text-white">
                      <div className="card-body text-center">
                        <i className="fa fa-exclamation-triangle fa-2x"></i>
                        <h4>{syncResults.summary.errors}</h4>
                        <small>Fouten</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-info text-white">
                      <div className="card-body text-center">
                        <i className="fa fa-list fa-2x"></i>
                        <h4>{syncResults.summary.totalProcessed}</h4>
                        <small>Totaal verwerkt</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sync details */}
            {syncResults.synced.length > 0 && (
              <div className="card mb-3">
                <div className="card-header bg-success text-white">
                  <i className="fa fa-check"></i> Successfully Synced ({syncResults.synced.length})
                </div>
                <div className="card-body" style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {syncResults.synced.map((device, index) => (
                    <div key={index} className="row border-bottom pb-2 mb-2">
                      <div className="col-md-6">
                        <strong>{device.name}</strong><br />
                        <code>{device.devEui}</code>
                      </div>
                      <div className="col-md-6">
                        <span className="badge badge-success">
                          {device.kampeerplaats.naam}
                        </span><br />
                        <small>Tags: place_id, plaats_naam</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {syncResults.errors.length > 0 && (
              <div className="card mb-3">
                <div className="card-header bg-danger text-white">
                  <i className="fa fa-exclamation-triangle"></i> Errors ({syncResults.errors.length})
                </div>
                <div className="card-body" style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {syncResults.errors.map((error, index) => (
                    <div key={index} className="row border-bottom pb-2 mb-2">
                      <div className="col-md-6">
                        <strong>{error.name || 'Unknown'}</strong><br />
                        <code>{error.devEui}</code>
                      </div>
                      <div className="col-md-6">
                        <span className="text-danger">{error.error}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <hr />

      <div className="row">
        <div className="col-md-4">
          <button type="button" className="btn btn-default" onClick={onBack}>
            <i className="fa fa-arrow-left"></i> Terug
          </button>
        </div>
        <div className="col-md-8 text-right">
          {!syncResults ? (
            <>
              <button
                type="button"
                className="btn btn-secondary mr-2"
                onClick={handleSkipSync}
              >
                <i className="fa fa-skip-forward"></i> Sync Overslaan
              </button>
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={handleSync}
                disabled={syncLoading}
              >
                {syncLoading ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i> Synchroniseren...
                  </>
                ) : (
                  <>
                    <i className="fa fa-sync"></i> Start Synchronisatie
                  </>
                )}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-success btn-lg"
              onClick={handleComplete}
            >
              <i className="fa fa-check"></i> Voltooien
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChirpStackStep4;